/**
 * Génération de fichiers d'export côté client, sans dépendance :
 *  - CSV compatible Excel (BOM UTF-8 + séparateur `;`, attendu par Excel en FR/BE) ;
 *  - PDF minimal (PDF 1.4, police Helvetica) pour les rapports imprimables.
 *
 * Ces deux formats couvrent les exports demandés (Excel/PDF) sans embarquer
 * SheetJS ni jsPDF, qui pèsent plusieurs centaines de kilo-octets dans un
 * bundle destiné à fonctionner hors connexion sur mobile.
 */

export interface Column<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
  /** Largeur relative, utilisée par l'export PDF. */
  width?: number;
}

function csvCell(value: string | number | null | undefined): string {
  if (value == null) return '';
  const text = String(value);
  if (/[";\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

export function toCsv<T>(rows: T[], columns: Column<T>[]): string {
  const header = columns.map((c) => csvCell(c.header)).join(';');
  const body = rows.map((row) => columns.map((c) => csvCell(c.value(row))).join(';'));
  // Le BOM force Excel à lire le fichier en UTF-8 (sinon les accents cassent).
  return `﻿${[header, ...body].join('\r\n')}\r\n`;
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Laisser au navigateur le temps de démarrer le téléchargement.
  setTimeout(() => URL.revokeObjectURL(url), 2_000);
}

export function downloadCsv<T>(rows: T[], columns: Column<T>[], filename: string): void {
  const blob = new Blob([toCsv(rows, columns)], { type: 'text/csv;charset=utf-8' });
  downloadBlob(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}

// ---------------------------------------------------------------------------
// PDF
// ---------------------------------------------------------------------------

/** Échappe les caractères réservés d'une chaîne littérale PDF. */
function pdfString(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/\(/g, '\\(').replace(/\)/g, '\\)');
}

/**
 * WinAnsiEncoding couvre le français. Les caractères hors table sont remplacés
 * par un équivalent ASCII pour éviter un rendu cassé.
 */
function toWinAnsi(value: string): string {
  return value
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201c\u201d]/g, '"')
    .replace(/[\u2013\u2014\u2212\u2012]/g, '-')
    .replace(/\u2026/g, '...')
    .replace(/[\u00a0\u202f\u2009]/g, ' ')
    .replace(/\u20ac/g, 'EUR')
    .replace(/[^\u0020-\u00ff]/g, '?');
}

interface PdfTextRun {
  text: string;
  x: number;
  y: number;
  size: number;
  bold: boolean;
}

const PAGE_WIDTH = 595.28; // A4 en points
const PAGE_HEIGHT = 841.89;
const MARGIN = 40;

export interface PdfReportOptions {
  title: string;
  subtitle?: string;
  /** Blocs de texte libres insérés avant le tableau. */
  paragraphs?: string[];
  footer?: string;
}

/**
 * Construit un PDF d'une ou plusieurs pages contenant un titre, des paragraphes
 * et un tableau. Volontairement limité : c'est un rapport, pas un moteur de mise
 * en page.
 */
export function buildPdf<T>(
  rows: T[],
  columns: Column<T>[],
  options: PdfReportOptions,
): Blob {
  const pages: PdfTextRun[][] = [];
  let current: PdfTextRun[] = [];
  let y = PAGE_HEIGHT - MARGIN;

  const newPage = () => {
    if (current.length > 0) pages.push(current);
    current = [];
    y = PAGE_HEIGHT - MARGIN;
  };

  const write = (text: string, size: number, bold = false, x = MARGIN) => {
    if (y < MARGIN + size * 2) newPage();
    current.push({ text: toWinAnsi(text), x, y, size, bold });
    y -= size * 1.6;
  };

  write(options.title, 18, true);
  if (options.subtitle) write(options.subtitle, 10);
  y -= 8;
  for (const paragraph of options.paragraphs ?? []) {
    for (const line of wrapText(paragraph, 95)) write(line, 10);
  }
  if ((options.paragraphs ?? []).length > 0) y -= 8;

  // Tableau : largeurs proportionnelles aux poids déclarés.
  const totalWeight = columns.reduce((sum, c) => sum + (c.width ?? 1), 0);
  const usable = PAGE_WIDTH - MARGIN * 2;
  const offsets: number[] = [];
  let cursor = MARGIN;
  for (const column of columns) {
    offsets.push(cursor);
    cursor += ((column.width ?? 1) / totalWeight) * usable;
  }

  const writeRow = (cells: string[], bold: boolean) => {
    if (y < MARGIN + 24) {
      newPage();
      columns.forEach((column, i) => {
        current.push({ text: toWinAnsi(column.header), x: offsets[i] ?? MARGIN, y, size: 9, bold: true });
      });
      y -= 16;
    }
    cells.forEach((cell, i) => {
      const width = ((columns[i]?.width ?? 1) / totalWeight) * usable;
      const maxChars = Math.max(4, Math.floor(width / 4.8));
      const clipped = cell.length > maxChars ? `${cell.slice(0, maxChars - 1)}…` : cell;
      current.push({ text: toWinAnsi(clipped), x: offsets[i] ?? MARGIN, y, size: 9, bold });
    });
    y -= 14;
  };

  writeRow(columns.map((c) => c.header), true);
  for (const row of rows) {
    writeRow(columns.map((c) => String(c.value(row) ?? '')), false);
  }

  if (options.footer) {
    y = MARGIN;
    current.push({ text: toWinAnsi(options.footer), x: MARGIN, y, size: 8, bold: false });
  }
  pages.push(current);

  return assemblePdf(pages);
}

function wrapText(text: string, maxChars: number): string[] {
  const words = text.split(/\s+/);
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (line.length + word.length + 1 > maxChars) {
      if (line) lines.push(line);
      line = word;
    } else {
      line = line ? `${line} ${word}` : word;
    }
  }
  if (line) lines.push(line);
  return lines.length > 0 ? lines : [''];
}

/** Sérialise les pages en un document PDF valide avec table des xref. */
function assemblePdf(pages: PdfTextRun[][]): Blob {
  const objects: string[] = [];
  const pageObjectIds: number[] = [];

  // 1 = catalogue, 2 = arbre des pages, 3 = Helvetica, 4 = Helvetica-Bold.
  const FONT_REGULAR = 3;
  const FONT_BOLD = 4;
  let nextId = 5;

  const contents: Array<{ id: number; body: string }> = [];
  for (const runs of pages) {
    const streamParts = runs.map((run) => {
      const font = run.bold ? '/FB' : '/FR';
      return `BT ${font} ${run.size} Tf 1 0 0 1 ${run.x.toFixed(2)} ${run.y.toFixed(2)} Tm (${pdfString(run.text)}) Tj ET`;
    });
    const stream = streamParts.join('\n');
    const contentId = nextId++;
    const pageId = nextId++;
    contents.push({ id: contentId, body: stream });
    pageObjectIds.push(pageId);
  }

  objects[1] = '<< /Type /Catalog /Pages 2 0 R >>';
  objects[2] = `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(' ')}] /Count ${pageObjectIds.length} >>`;
  objects[FONT_REGULAR] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>';
  objects[FONT_BOLD] = '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold /Encoding /WinAnsiEncoding >>';

  contents.forEach((content, index) => {
    const pageId = pageObjectIds[index]!;
    objects[content.id] = `<< /Length ${byteLength(content.body)} >>\nstream\n${content.body}\nendstream`;
    objects[pageId] =
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_WIDTH} ${PAGE_HEIGHT}] ` +
      `/Resources << /Font << /FR ${FONT_REGULAR} 0 R /FB ${FONT_BOLD} 0 R >> >> ` +
      `/Contents ${content.id} 0 R >>`;
  });

  let pdf = '%PDF-1.4\n';
  const offsets: number[] = [];
  for (let id = 1; id < objects.length; id += 1) {
    const body = objects[id];
    if (!body) continue;
    offsets[id] = byteLength(pdf);
    pdf += `${id} 0 obj\n${body}\nendobj\n`;
  }

  const xrefOffset = byteLength(pdf);
  const maxId = objects.length;
  pdf += `xref\n0 ${maxId}\n0000000000 65535 f \n`;
  for (let id = 1; id < maxId; id += 1) {
    const offset = offsets[id] ?? 0;
    pdf += `${String(offset).padStart(10, '0')} 00000 n \n`;
  }
  pdf += `trailer\n<< /Size ${maxId} /Root 1 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`;

  return new Blob([pdf], { type: 'application/pdf' });
}

function byteLength(value: string): number {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(value).length;
  return value.length;
}

export function downloadPdf<T>(
  rows: T[],
  columns: Column<T>[],
  options: PdfReportOptions,
  filename: string,
): void {
  downloadBlob(buildPdf(rows, columns, options), filename.endsWith('.pdf') ? filename : `${filename}.pdf`);
}

// ---------------------------------------------------------------------------
// Fichier .ics (ajouter au calendrier — app Parents)
// ---------------------------------------------------------------------------

/**
 * Fuseau de la section. Les horaires sont stockés en UTC, donc sans ambiguïté ;
 * mais une activité « sur toute la journée » s'écrit en date seule, et il faut
 * ce fuseau pour savoir de quel jour il s'agit — le 31 juillet à minuit à
 * Bastogne est encore le 30 juillet en temps universel.
 */
export const SECTION_TIME_ZONE = 'Europe/Brussels';

/** `en-CA` produit AAAA-MM-JJ, le seul format ISO qu'Intl sait rendre tel quel. */
const icsDayFormat = new Intl.DateTimeFormat('en-CA', {
  timeZone: SECTION_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function icsDate(value: string): string {
  return new Date(value).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
}

function icsDay(value: string, plusDays = 0): string {
  const date = new Date(new Date(value).getTime() + plusDays * 86_400_000);
  return icsDayFormat.format(date).replace(/-/g, '');
}

function icsEscape(value: string): string {
  return value.replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

/**
 * Repli des lignes à 75 octets (RFC 5545 §3.1). Une description un peu longue
 * dépasse vite la limite, et certains agendas tronquent silencieusement la
 * ligne au lieu de la lire en entier.
 *
 * La limite est en octets, pas en caractères : « é » en compte deux. On
 * parcourt donc par point de code pour ne jamais couper un caractère en deux.
 */
function icsFold(line: string): string {
  const encoder = new TextEncoder();
  if (encoder.encode(line).length <= 75) return line;

  const parts: string[] = [];
  let current = '';
  let bytes = 0;
  for (const char of line) {
    const size = encoder.encode(char).length;
    // 75 octets sur la première ligne ; 74 ensuite, l'espace de continuation
    // comptant dans la limite.
    const limit = parts.length === 0 ? 75 : 74;
    if (bytes + size > limit) {
      parts.push(current);
      current = '';
      bytes = 0;
    }
    current += char;
    bytes += size;
  }
  parts.push(current);
  return parts.join('\r\n ');
}

export interface CalendarEventInput {
  id: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startAt: string;
  endAt: string;
  /** Journée entière : écrite en dates seules, sans heure ni fuseau. */
  allDay?: boolean;
  /** Dernière modification, pour qu'un agenda abonné voie qu'elle a changé. */
  updatedAt?: string | null;
}

export function buildIcs(events: CalendarEventInput[], calendarName = "Bala'App"): string {
  const stamp = icsDate(new Date().toISOString());
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    "PRODID:-//Bala'App//FR",
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${icsEscape(calendarName)}`,
    `X-WR-TIMEZONE:${SECTION_TIME_ZONE}`,
    // Un agenda abonné décide seul de sa fréquence de relecture ; ces deux
    // lignes lui suggèrent six heures. La première est la forme normalisée, la
    // seconde celle que comprennent Outlook et quelques autres.
    'REFRESH-INTERVAL;VALUE=DURATION:PT6H',
    'X-PUBLISHED-TTL:PT6H',
  ];
  for (const event of events) {
    lines.push('BEGIN:VEVENT', `UID:${event.id}@balaapp`, `DTSTAMP:${stamp}`);
    if (event.allDay) {
      // DTEND est exclusif pour une date seule : le lendemain du dernier jour.
      lines.push(`DTSTART;VALUE=DATE:${icsDay(event.startAt)}`, `DTEND;VALUE=DATE:${icsDay(event.endAt, 1)}`);
    } else {
      lines.push(`DTSTART:${icsDate(event.startAt)}`, `DTEND:${icsDate(event.endAt)}`);
    }
    lines.push(`SUMMARY:${icsEscape(event.title)}`);
    if (event.description) lines.push(`DESCRIPTION:${icsEscape(event.description)}`);
    if (event.location) lines.push(`LOCATION:${icsEscape(event.location)}`);
    if (event.updatedAt) lines.push(`LAST-MODIFIED:${icsDate(event.updatedAt)}`);
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  // Le CRLF est requis par la RFC 5545, y compris après la dernière ligne.
  return `${lines.map(icsFold).join('\r\n')}\r\n`;
}

// ---------------------------------------------------------------------------
// Abonnement calendrier
// ---------------------------------------------------------------------------

/**
 * Les deux adresses du flux d'abonnement, composées depuis l'origine de la page
 * courante — c'est la seule adresse dont on sait qu'elle est joignable depuis
 * l'appareil qui lit l'écran.
 *
 * `webcal:` est un `https:` déguisé : les agendas d'Apple, Google et Outlook s'y
 * abonnent d'un seul geste, là où un lien `https:` ouvre un téléchargement.
 */
export function calendarFeedUrls(token: string, baseOrigin?: string): { https: string; webcal: string } {
  let origin = baseOrigin;
  if (!origin && typeof window !== 'undefined' && window.location?.origin) {
    origin = window.location.origin;
  }
  if (!origin) {
    origin = 'https://bala-app.be';
  }

  // Si baseOrigin contient un chemin (ex. '/api' ou 'https://api.../api'), extraire l'origine
  if (origin.startsWith('http://') || origin.startsWith('https://')) {
    try {
      origin = new URL(origin).origin;
    } catch {
      // conserver origin
    }
  }

  // Remplacer strictement toute éventuelle génération dynamique en http:// par https://
  origin = origin.replace(/^http:\/\//i, 'https://');

  const url = new URL(`/api/calendar/${encodeURIComponent(token)}.ics`, origin);
  // Forcer l'utilisation exclusive du protocole sécurisé https://
  url.protocol = 'https:';

  const httpsString = url.toString().replace(/^http:\/\//i, 'https://');
  const webcalString = httpsString.replace(/^https?:/i, 'webcal:');

  return { https: httpsString, webcal: webcalString };
}

/**
 * Vrai si l'adresse courante n'est joignable que depuis cet appareil ou ce
 * réseau. Un agenda en ligne relit le flux depuis ses propres serveurs : il ne
 * verra jamais un `localhost`, et l'abonnement échouera sans rien dire.
 */
export function isLocalOrigin(hostname = window.location.hostname): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '::1' ||
    hostname.endsWith('.local') ||
    /^127\./.test(hostname) ||
    /^10\./.test(hostname) ||
    /^192\.168\./.test(hostname) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(hostname)
  );
}

/** Copie dans le presse-papiers. Faux si le navigateur l'a refusé. */
export async function copyToClipboard(text: string): Promise<boolean> {
  // `navigator.clipboard` n'existe qu'en contexte sécurisé, et l'utilisateur
  // peut refuser la permission : l'appelant doit prévoir d'afficher le texte.
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export function downloadIcs(events: CalendarEventInput[], filename: string, calendarName?: string): void {
  const blob = new Blob([buildIcs(events, calendarName)], { type: 'text/calendar;charset=utf-8' });
  downloadBlob(blob, filename.endsWith('.ics') ? filename : `${filename}.ics`);
}
